import { AdjustBlur, AdjustShape } from './adjust.types'

/** Round-capped polyline through flat [x, y, x, y, …] points (a single point draws a dot) */
export const tracePolyline = (ctx: CanvasRenderingContext2D, points: number[]): void => {
  ctx.beginPath()
  if (points.length < 2) return
  ctx.moveTo(points[0], points[1])
  if (points.length === 2) {
    ctx.lineTo(points[0] + 0.01, points[1])
    return
  }
  for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1])
}

/** [x0, y0, x1, y1] → left, top, width, height */
export const normaliseRect = (points: number[]): [number, number, number, number] => {
  const [x0 = 0, y0 = 0, x1 = x0, y1 = y0] = points
  return [Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0)]
}

/** Arrow head as in the in-house editor: filled, length max(10, 3 × stroke), 30° half-angle */
const drawArrowHead = (ctx: CanvasRenderingContext2D, fromX: number, fromY: number, toX: number, toY: number, size: number) => {
  const angle = Math.atan2(toY - fromY, toX - fromX)
  const length = Math.max(10, size * 3)
  const spread = Math.PI / 6
  ctx.beginPath()
  ctx.moveTo(toX + Math.cos(angle) * size * 0.5, toY + Math.sin(angle) * size * 0.5)
  ctx.lineTo(toX - length * Math.cos(angle - spread), toY - length * Math.sin(angle - spread))
  ctx.lineTo(toX - length * Math.cos(angle + spread), toY - length * Math.sin(angle + spread))
  ctx.closePath()
  ctx.fill()
}

/** Draws one shape in IMAGE coordinates (the caller sets the transform). The eraser cuts earlier strokes. */
export const drawAdjustShape = (ctx: CanvasRenderingContext2D, shape: AdjustShape): void => {
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.lineWidth = shape.size
  ctx.strokeStyle = shape.color
  ctx.fillStyle = shape.color
  ctx.globalAlpha = shape.tool === 'eraser' ? 1 : shape.opacity
  if (shape.tool === 'eraser') ctx.globalCompositeOperation = 'destination-out'

  const [x0 = 0, y0 = 0, x1 = x0, y1 = y0] = shape.points
  switch (shape.tool) {
    case 'pen':
    case 'eraser':
      tracePolyline(ctx, shape.points)
      ctx.stroke()
      break
    case 'line':
    case 'arrow':
      ctx.beginPath()
      ctx.moveTo(x0, y0)
      ctx.lineTo(x1, y1)
      ctx.stroke()
      if (shape.tool === 'arrow') drawArrowHead(ctx, x0, y0, x1, y1, shape.size)
      break
    case 'rectangle': {
      const [left, top, width, height] = normaliseRect(shape.points)
      ctx.strokeRect(left, top, width, height)
      break
    }
    case 'ellipse': {
      const [left, top, width, height] = normaliseRect(shape.points)
      ctx.beginPath()
      ctx.ellipse(left + width / 2, top + height / 2, Math.max(0.5, width / 2), Math.max(0.5, height / 2), 0, 0, Math.PI * 2)
      ctx.stroke()
      break
    }
  }
  ctx.restore()
}

/** A shape too small to see (a click without a drag) */
export const isDegenerateShape = (shape: AdjustShape, minLength: number): boolean => {
  if (shape.tool === 'pen' || shape.tool === 'eraser') return false
  const [x0 = 0, y0 = 0, x1 = x0, y1 = y0] = shape.points
  return Math.hypot(x1 - x0, y1 - y0) < minLength
}

/** Fills the blurred area (white) in IMAGE coordinates */
export const traceBlurMask = (ctx: CanvasRenderingContext2D, blur: AdjustBlur): void => {
  ctx.fillStyle = '#FFFFFF'
  ctx.strokeStyle = '#FFFFFF'
  if (blur.mode === 'area') {
    const [left, top, width, height] = normaliseRect(blur.points)
    ctx.fillRect(left, top, width, height)
    return
  }
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.lineWidth = blur.size
  tracePolyline(ctx, blur.points)
  ctx.stroke()
}

/** IMAGE-space bounds a blur reaches, its blur radius included */
export const blurBounds = (blur: AdjustBlur): [number, number, number, number] => {
  const xs = blur.points.filter((_, i) => i % 2 === 0)
  const ys = blur.points.filter((_, i) => i % 2 === 1)
  const reach = (blur.mode === 'brush' ? blur.size / 2 : 0) + blur.radius * 2
  const left = Math.min(...xs) - reach
  const top = Math.min(...ys) - reach
  return [left, top, Math.max(...xs) + reach - left, Math.max(...ys) + reach - top]
}

export const isDegenerateBlur = (blur: AdjustBlur, minLength: number): boolean => {
  if (blur.mode === 'brush') return false
  const [, , width, height] = normaliseRect(blur.points)
  return width < minLength || height < minLength
}
